/**
 * Gyro's page-wide keys leave the host page's own fields alone (M-10, raised by All-Hands on 2026-10-07). Gyro listens for
 * Ctrl/Cmd+J and Ctrl/Cmd+Shift+V or +J on the whole document, and Ctrl/Cmd+Shift+V is paste as plain text in a text field:
 * Gyro used to take it for voice there. A key from one of the host's fields is now the page's. Gyro cannot see into a
 * closed shadow root, so a key from inside one is the page's too, whatever has the focus there. Gyro's own composer and
 * fields keep every shortcut.
 */
/** Input types that take no text: a key on one of them is not typing. */
const NON_TEXT_INPUT_TYPES = new Set(['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit']);
function isElement(node) {
    return typeof node === 'object' && node !== null && node.nodeType === 1 && typeof node.getAttribute === 'function';
}
/** A field: an input that takes text, a textarea, a select, or an editable region (contenteditable). */
function isField(element, ancestors) {
    const name = element.localName;
    if (name === 'textarea' || name === 'select')
        return true;
    if (name === 'input')
        return !NON_TEXT_INPUT_TYPES.has((element.type || 'text').toLowerCase());
    const editable = element.isContentEditable;
    if (typeof editable === 'boolean')
        return editable;
    // A DOM without isContentEditable (jsdom): the nearest contenteditable attribute on the way up decides.
    for (const node of ancestors) {
        if (!isElement(node))
            continue;
        const value = node.getAttribute('contenteditable');
        if (value !== null)
            return value.toLowerCase() !== 'false';
    }
    return false;
}
/**
 * Whether the focus is in one of Gyro's own elements while they sit in a shadow root. A closed root hides them from the
 * document's composed path, but Gyro holds them, so it can ask their root what has the focus there.
 */
function focusInGyroShadowRoot(own) {
    return own.some((element) => {
        const root = element.getRootNode();
        if (!root.host)
            return false; // in the document itself: the path shows Gyro's elements
        const active = root.activeElement;
        return active ? element.contains(active) : false;
    });
}
/**
 * Whether the focus is inside a shadow tree the document cannot see (a closed shadow root) whose host is `origin`, the
 * first element of a key's composed path as the document sees it. A key from inside such a tree reaches the document with
 * only its host, which is no field. The signs: the host is the focused element of its own tree (the focus is on it or
 * inside it) and has no open shadow root. A custom element (a name with a hyphen) may then hold a field inside it, so the
 * key is the page's. Any other element holds the focus inside it only when it cannot take the focus itself (no tabindex,
 * not a link or a control). A plain element the browser focused itself (a <dialog> with nothing focusable in it, a
 * scrolling region) passes too, which leaves the key to the page: the safe side. The page's body never passes.
 */
function focusHiddenInside(origin) {
    const page = origin.ownerDocument;
    if (origin === page.body || origin === page.documentElement)
        return false;
    if (origin.shadowRoot)
        return false; // open: the path would show where
    const root = origin.getRootNode();
    if (root.activeElement !== origin)
        return false;
    if (origin.localName.includes('-'))
        return true;
    const tabIndex = origin.tabIndex;
    return !origin.hasAttribute('tabindex') && !(typeof tabIndex === 'number' && tabIndex >= 0);
}
/**
 * Whether a key event comes from one of the host page's own fields: an input that takes text, a textarea, a select or an
 * editable region, outside Gyro's own elements (`gyroElements`: its panel and its launcher). The event's composed path says
 * where it really began, also inside an open shadow root (a host's web component, or Gyro mounted in one), where the
 * document sees only the shadow host as its target. A closed shadow root hides where the key began, so a key from inside
 * one counts as the page's (focusHiddenInside), unless the focus is in Gyro's own elements there. A key that passes through
 * one of Gyro's elements is Gyro's. Never throws.
 */
export function keyFromHostField(event, gyroElements) {
    try {
        const own = gyroElements.filter((element) => isElement(element));
        const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
        if (path.length > 0) {
            if (path.some((node) => own.includes(node)))
                return false;
            if (focusInGyroShadowRoot(own))
                return false;
            const origin = path.find(isElement);
            if (!origin)
                return false;
            return isField(origin, path) || focusHiddenInside(origin);
        }
        // No composed path (an event read after its dispatch): the target and its ancestors in its own tree.
        const target = event.target;
        if (!isElement(target) || own.some((element) => element.contains(target)))
            return false;
        const ancestors = [];
        for (let node = target; node; node = node.parentElement)
            ancestors.push(node);
        return isField(target, ancestors);
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=hostFields.js.map